import { useState } from 'react'
import { Alert, Button, CircularProgress, Container, Paper, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import {
  CONTACT_VERIFICATION_PHONE_KEY,
  CONTACT_VERIFICATION_PHONE_VALIDATED_KEY,
  setItemToLS,
  useRequestContactVerificationPhoneToken,
  useValidateContactVerificationPhoneToken,
} from '../../core/api'

export default function VerificationPhoneCard() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { verificationToken = '' } = useParams()
  const requestPhoneToken = useRequestContactVerificationPhoneToken()
  const validatePhoneToken = useValidateContactVerificationPhoneToken()
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [phoneTokenRequested, setPhoneTokenRequested] = useState(false)
  const [phoneValidated, setPhoneValidated] = useState(false)

  function handleUseOtherPhoneNumber() {
    setPhone('')
    setOtp('')
    setError('')
    setPhoneTokenRequested(false)
  }

  async function handleRequestPhoneToken(event) {
    event.preventDefault()
    setError('')

    try {
      await requestPhoneToken.mutateAsync({ phone: phone.trim() })
      setItemToLS(CONTACT_VERIFICATION_PHONE_KEY, phone.trim())
      setPhoneTokenRequested(true)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t('verification.phoneRequestError'))
    }
  }

  async function handleValidatePhoneToken(event) {
    event.preventDefault()
    setError('')

    try {
      const result = await validatePhoneToken.mutateAsync({ token: otp.trim() })

      if (!result?.valid) {
        setError(t('verification.phoneOtpInvalid'))
        return
      }

      setItemToLS(CONTACT_VERIFICATION_PHONE_VALIDATED_KEY, true)
      setPhoneValidated(true)
      navigate(`/verify/${verificationToken}/review`)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || t('verification.phoneValidateError'))
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
        <Stack spacing={2.5}>
          <div>
            <Typography variant="h3" gutterBottom>
              {t('verification.phoneTitle')}
            </Typography>
            <Typography color="text.secondary">{t('verification.phoneDescription')}</Typography>
          </div>
          {phoneTokenRequested ? <Alert severity="info">{t('verification.phoneTokenRequested')}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {phoneValidated ? (
            <Alert severity="success">{t('verification.phoneValidated')}</Alert>
          ) : (
            <>
              <Stack component="form" spacing={2} onSubmit={handleRequestPhoneToken}>
                <TextField
                  autoComplete="tel"
                  fullWidth
                  label={t('verification.phoneLabel')}
                  name="phone"
                  onChange={(event) => setPhone(event.target.value)}
                  required
                  value={phone}
                  disabled={requestPhoneToken.isPending || phoneTokenRequested}
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    disabled={requestPhoneToken.isPending}
                    loading={requestPhoneToken.isPending}
                    type="submit"
                    variant="contained"
                    color={phoneTokenRequested ? 'inherit' : 'primary'}
                  >
                    {phoneTokenRequested ? t('verification.resendPhoneToken') : t('verification.requestPhoneToken')}
                  </Button>
                  {phoneTokenRequested ? (
                    <Button
                      fullWidth
                      disabled={requestPhoneToken.isPending}
                      loading={requestPhoneToken.isPending}
                      onClick={handleUseOtherPhoneNumber}
                      variant="text"
                    >
                      {t('verification.useOtherPhoneNumber')}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              {phoneTokenRequested ? (
                <Stack component="form" spacing={2} onSubmit={handleValidatePhoneToken}>
                  <TextField
                    autoComplete="one-time-code"
                    fullWidth
                    label={t('verification.phoneOtpLabel')}
                    name="otp"
                    onChange={(event) => setOtp(event.target.value)}
                    required
                    value={otp}
                  />
                  <Button loading={validatePhoneToken.isPending} type="submit" variant="contained" color="primary">
                    {t('verification.validatePhoneToken')}
                  </Button>
                </Stack>
              ) : null}
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  )
}
